import { motion } from "framer-motion";
import { Reveal, SectionHead } from "@/components/Reveal";
import { Icon } from "@/components/Icon";
import { FARMER_BENEFITS, RETAILER_BENEFITS, HOW_IT_WORKS, WHY_MARKETPLACE, PRODUCT_CATEGORIES, TESTIMONIALS } from "@/data/site";

const Column = ({ title, items, testid }) => (
  <div>
    <Reveal>
      <p className="font-mono-label text-[10px] uppercase tracking-[0.2em] text-[color:var(--kmg-gold)]">{title}</p>
    </Reveal>
    <div className="mt-8 space-y-7">
      {items.map((b, i) => (
        <Reveal key={b.title} delay={i * 0.05}>
          <div className="flex gap-4" data-testid={`${testid}-${i}`}>
            <Icon name={b.icon} size={18} className="text-[color:var(--kmg-lime)] shrink-0 mt-1" />
            <div>
              <h4 className="font-display text-base">{b.title}</h4>
              <p className="text-sm mt-1.5 text-[color:var(--kmg-bone-dim)] leading-relaxed">{b.body}</p>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  </div>
);

export const Marketplace = ({ onJoin }) => (
  <section id="marketplace" className="relative py-32 sm:py-40 bg-[color:var(--kmg-deep)] grain" data-testid="marketplace-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHead
        eyebrow="02 — The KMG Digi Marketplace"
        title="Connect. Trade. Grow. One platform, endless possibilities."
        sub="Bringing farmers and retailers together on one digital platform to buy, sell and grow — efficiently, transparently and profitably."
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 mt-20">
        <Column title="Benefits for Farmers" items={FARMER_BENEFITS} testid="farmer-benefit" />
        <Column title="Benefits for Retailers" items={RETAILER_BENEFITS} testid="retailer-benefit" />
      </div>

      <div className="mt-28">
        <Reveal>
          <p className="eyebrow">How the marketplace works</p>
        </Reveal>
        <div className="relative mt-12">
          <motion.div
            className="hidden md:block absolute left-0 right-0 top-6 h-px bg-[color:var(--kmg-lime)] origin-left"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
            style={{ opacity: 0.3 }}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10">
            {HOW_IT_WORKS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.1}>
                <div data-testid={`how-step-${s.n}`}>
                  <span className="relative z-10 grid place-items-center w-12 h-12 rounded-full bg-[color:var(--kmg-lime)] text-[color:var(--kmg-forest)] font-display text-lg">
                    {s.n}
                  </span>
                  <h4 className="font-display text-base mt-6">{s.title}</h4>
                  <p className="text-sm mt-2.5 text-[color:var(--kmg-bone-dim)] leading-relaxed">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-28">
        {PRODUCT_CATEGORIES.map((c, i) => (
          <Reveal key={c.id} delay={i * 0.07}>
            <div
              data-testid={`category-card-${c.id}`}
              className="lift relative h-56 overflow-hidden border border-[rgba(111,227,155,0.16)] hover:border-[color:var(--kmg-lime)]"
            >
              <img src={c.image} alt={c.name} className="w-full h-full object-cover" style={{ filter: "brightness(.58)" }} />
              <span className="absolute bottom-5 left-5 font-display text-xl">{c.name}</span>
            </div>
          </Reveal>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-20">
        {TESTIMONIALS.map((t, i) => (
          <Reveal key={t.author} delay={i * 0.1}>
            <blockquote data-testid={`testimonial-${i}`} className="border-l-2 border-[color:var(--kmg-lime)] pl-7 py-3">
              <p className="font-editorial italic text-xl sm:text-2xl leading-snug">"{t.quote}"</p>
              <footer className="font-mono-label text-[10px] uppercase tracking-[0.2em] mt-5 text-[color:var(--kmg-gold)]">— {t.author}</footer>
            </blockquote>
          </Reveal>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-8 mt-28 border-t border-[rgba(244,241,230,0.14)] pt-14">
        {WHY_MARKETPLACE.map((w, i) => (
          <Reveal key={w.title} delay={i * 0.06}>
            <div data-testid={`why-marketplace-${i}`}>
              <Icon name={w.icon} size={20} className="text-[color:var(--kmg-lime)]" />
              <h4 className="font-display text-base mt-5 leading-snug">{w.title}</h4>
              <p className="text-sm mt-2 text-[color:var(--kmg-bone-dim)]">{w.body}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.1}>
        <div className="mt-24 border border-[rgba(111,227,155,0.2)] bg-[color:var(--kmg-forest)] p-10 sm:p-14 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
          <div>
            <p className="eyebrow">Stronger together. Profitable together.</p>
            <h3 className="font-display text-2xl sm:text-3xl mt-4 max-w-xl leading-snug">
              Join thousands of farmers and retailers already benefiting from the KMG Digi Marketplace.
            </h3>
          </div>
          <button
            data-testid="marketplace-cta-btn"
            onClick={onJoin}
            className="shrink-0 rounded-full bg-[color:var(--kmg-lime)] px-9 py-4 text-sm font-semibold text-[color:var(--kmg-forest)] hover:bg-[#8df0b3]"
            style={{ transition: "background-color .4s ease" }}
          >
            Register now
          </button>
        </div>
      </Reveal>
    </div>
  </section>
);
